import CommanderBase from "../framework/src/commander/CommanderBase";
import events from "../framework/src/events/EventsObservable";
import States from "./States.js";
import TouchZone from "./TouchZone.js";
import InfoPages from "./InfoPages.js";
import Lights from "./Lights.js";
import WinController from "./win/WinController";
import SymbolAnim from "./symbols/SymbolAnim.js";
import { REELS_EVENTS } from "../framework/src/game/slot/reelsEvents";
import { CONFIG_PARAMS } from "../framework/src/system/SystemConfig";
import { GAME_EVENTS } from "../framework/src/game/gameEvents";
import { WALLET_EVENTS } from "../framework/src/wallet/Wallet";
import { pixiSetTimeout } from "../framework/src/engine/pixi/pixiTimeout";
import Counter from "./win/Counter";
import { STATES } from "../framework/src/states/states";
import SERVICE_IDENTIFIER from "../framework/src/constants/identifiers";
import IInfoPages from "../framework/src/engine/IInfoPages";
import { injectable } from "inversify";
import AbstractIframePage from "../framework/src/engine/AbstractIfarmePage";

class Commander extends CommanderBase {
	slots: any;
	layers: any;
	infoSymbols: any;
	factors: any;

	touchZone: TouchZone;
	counter: Counter;
	infopages: AbstractIframePage;
	paragraph: AbstractIframePage;

	winSymbolSprites: Array<any>;
	WinController: WinController;
	symbolAnim: SymbolAnim;
	lights: Lights;

	constructor(system, game, engine) {
		super(system, game, engine);

		this.slots = game.gameCore.slots;

		// @ts-ignore
		this.layers = this.app.layers.getAllLayers();

		this.infoSymbols = [];
		this.factors = [];
	}

	/**
	 * @override
	 */
	create(container) {
		super.create(container);

		// @ts-ignore
		this.infopages = container.get<AbstractIframePage>(
			SERVICE_IDENTIFIER.InfoPages
		);
		this.infopages.create();
		// @ts-ignore
		this.paragraph = container.get<AbstractIframePage>(
			SERVICE_IDENTIFIER.ParagraphPage
		);
		this.paragraph.create();

		this.touchZone = new TouchZone(
			this.config,
			this.layers,
			this.slots,
			this.wallet,
			this.symbols,
			this.infopages
		);
		this.counter = new Counter(
			this.wallet,
			this.layers,
			this.config,
			this.audio
		);

		this.winSymbolSprites = [
			[undefined, undefined, undefined],
			[undefined, undefined, undefined],
			[undefined, undefined, undefined],
			[undefined, undefined, undefined],
		];

		this.WinController = new WinController(this);
		this.symbolAnim = new SymbolAnim(this);
		this.lights = new Lights(this);

		let states = new States(this);

		events.subscribe(GAME_EVENTS.GAME_SPIN_START, () => this.onSpinStart());
		events.subscribe(GAME_EVENTS.GAME_END_ROUND_RECALCED, (eventName, data) =>
			this.onEndRoundRecalced(data)
		);

		events.subscribe(REELS_EVENTS.REEL_SPIN, (eventName, data) =>
			states.spin.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_BRAKEDOWN, (eventName, data) =>
			states.brakeDown.call(this, data)
		);
		events.subscribe(REELS_EVENTS.REEL_STOP, (eventName, data) =>
			states.stop.call(this, data)
		);

		events.subscribe(WALLET_EVENTS.WALLET_ON_SET_BET, this);

		createjs.MotionGuidePlugin.install(); // without this moving on a bezier curve is not working

		this.layers.reels.filters = [new PIXI.filters.ColorMatrixFilter()];

		this.WinController.init();
		this.lights.create();
		this.counter.create();
		this.symbolAnim.init();
		this.touchZone.zonesOn();
	}

	/**
	 * Functions called on every spin start.
	 * Disable TouchZone and InfoPage arrows.
	 * Change background lights animation.
	 * @override
	 */
	onSpinStart() {
		this.audio.playTrack("reel_Spin");
		this.touchZone.zonesOff();
		this.touchZone.hide();
		this.lights.setState("spin");
		for (let reel of this.slots.getReels()) {
			let symbols = reel.getSymbols();
			symbols[0].visible = symbols[symbols.length - 1].visible = true;
		}
	}

	notify(eventName, data) {
		switch (eventName) {
			case WALLET_EVENTS.WALLET_ON_SET_BET:
				this.touchZone.updateBet();
				break;
		}

		super.notify(eventName, data);
	}

	skip() {
		this.WinController.skip = true;
	}

	/**
	 * Get the result. See if there are any win lines.
	 * If there are any winlines, run the winning animations or do nothing.
	 * @param result
	 */
	controlResult(result) {
		// @ts-ignore
		this.audio.stopTrack("reel_Spin");

		if (result.lineWins && result.lineWins.length) {
			this.onControlWin(result);
			return;
		}

		this.onControlNoWin();
	}

	/**
	 * Change background lights animation. Draw win symbols and run winning animations.
	 * Update win value.
	 * @param result
	 */
	onControlWin(result) {
		this.lights.setState("win");
		this.WinController.setWinSymbols(result);
		this.WinController.run(result).then(async () => {
			this.setWin(result.totalWin);
			if (this.isBigWin(result.win)) {
				this.states.setState(STATES.BONUS);
				await this.counter.bigwin(result.win);
				this.states.setState(STATES.BONUS);
			}
			pixiSetTimeout(0.5, () => {
				this.endRound();
			});
		});
	}

	isBigWin(win) {
		return (
			win / this.wallet.getBet() >=
			this.config.get(CONFIG_PARAMS.BIG_WIN_FACTOR)
		);
	}

	/**
	 * On no win do nothing.
	 */
	onControlNoWin() {
		this.endRound();
	}

	/**
	 * Call {@link CommanderBase#endRound} that sends the update message to server.
	 * After that, set Lights.state to idle to change the animation.
	 * Enable TouchZones and InfoPage arrows.
	 */
	onEndRoundRecalced({ autorun }) {
		if (!autorun) {
			this.lights.setState("idle");
			this.touchZone.zonesOn();
		}
		this.cleanUp();
	}

	/**
	 * Return symbol factor from symbolDefinition array by name from given symbol.
	 * @param name {PIXI.Sprite}
	 * @return {array}
	 */
	getSymbolFactor(name) {
		return this.config
			.get(CONFIG_PARAMS.SYMBOL_DEFINITION)
			.find((symbols) => symbols.name === name);
	}

	/**
	 * Set texture of symbol to blurred version. Called by {@link States#spin}
	 * @param symbol {PIXI.Sprite}
	 * @return {*}
	 */
	blurSymbol(symbol) {
		symbol.texture = PIXI.Texture.from(symbol.name + "_blur.png");
		return symbol;
	}

	/**
	 * Set texture of symbol to normal. Called by {@link States#brakeUp}.
	 * @param symbol {PIXI.Sprite}
	 * @return {*}
	 */
	unblurSymbol(symbol) {
		symbol.name = symbol.name.replace("_blur", "");
		symbol.texture = PIXI.Texture.from(symbol.name + ".png");
		return symbol;
	}

	/**
	 * Change the brightness value of the filter applied to reels layer.
	 * @param brightness
	 */
	reelsBrightness(brightness) {
		this.layers.reels.filters[0].brightness(brightness);
	}

	cleanUp() {
		this.winSymbolSprites = [
			[undefined, undefined, undefined],
			[undefined, undefined, undefined],
			[undefined, undefined, undefined],
			[undefined, undefined, undefined],
		];
		this.WinController.removeWinSymbols();
	}

	// _______________________________________________
	// STATIC METHODS
	//

	static getSprite(name, x, y, anchor = { x: 0.5, y: 0.5 }) {
		let sprite = new PIXI.Sprite(PIXI.Texture.from(name));
		sprite.anchor.set(anchor.x, anchor.y);
		sprite.position.set(x, y);
		return sprite;
	}

	static removeTween(sprite) {
		createjs.Tween.removeTweens(sprite);
	}

	static setAlpha(obj, value, time) {
		this.removeTween(obj);
		createjs.Tween.get(obj)
			.to({ alpha: value }, time)
			.call(() => this.removeTween(obj));
	}

	static getTween(
		obj,
		to,
		time,
		eventL = () => {},
		cb = () => {},
		ease = createjs.Ease.linear
	) {
		Commander.removeTween(obj);
		let tween = createjs.Tween.get(obj)
			.to(to, time, ease)
			.call(() => {
				Commander.removeTween(obj);
				cb();
			})
			.addEventListener("change", eventL);
	}

	static getTweenAsync(
		obj,
		to,
		time,
		eventL = () => {},
		ease = createjs.Ease.linear
	) {
		return new Promise((res) => {
			Commander.removeTween(obj);
			let tween = createjs.Tween.get(obj)
				.to(to, time, ease)
				.call(() => {
					Commander.removeTween(obj);
					res();
				})
				.addEventListener("change", eventL);
		});
	}

	static getLoop(
		obj,
		to1,
		to2,
		time,
		eventL = () => {},
		ease = createjs.Ease.linear
	) {
		Commander.removeTween(obj);
		return createjs.Tween.get(obj, { loop: true })
			.to(to1, time / 2, ease)
			.to(to2, time / 2, ease)
			.addEventListener("change", eventL);
	}

	static removeSprite(sprite) {
		Commander.removeTween(sprite.scale);
		Commander.removeTween(sprite);
		if (sprite !== undefined && sprite.parent !== undefined) {
			sprite.parent.removeChild(sprite);
			sprite.destroy({ children: true, texture: false, baseTexture: true });
		}
	}

	static cleanArray(array) {
		array.length = 0;
	}
}
export default Commander;
